import { Box, MenuItem, TextField } from '@mui/material'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { paymentsApi } from '../../api/swagger'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import {
  ActorFilterOption,
  CommunityFilterOption,
  ProgramType,
} from '../../swagger'
import { BillingFilters } from '../Account/Billing/interfaces'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import { TextButtonVariant } from '../Buttons/TextButton'
import DebouncedAutocomplete from '../Elements/DebouncedAutocomplete'
import BasicModal from './BasicModal'
import { formatActorOption } from './utils/formatActorOptions'

export type OnFilterChange = <T extends keyof BillingFilters>(
  name: T,
  value: BillingFilters[T]
) => void

type LicensingFiltersModalProps = {
  isOpen: boolean
  onClose: () => void
  onFilter: (values: BillingFilters) => void
  actorKey: number
  showActorFilter?: boolean
}

const LicensingFiltersModal = forwardRef<
  { clearFilter: (filterName: keyof BillingFilters) => void },
  LicensingFiltersModalProps
>(({ isOpen, onClose, onFilter, actorKey, showActorFilter = true }, ref) => {
  const { t } = useTranslation()
  const defaultSearchActorLabel = t(
    'LicensingFiltersModal.ActorSearch.Label',
    'Search a Director'
  )
  const defaultNoActorFoundMessage = t(
    'LicensingFiltersModal.ActorSearch.NoResults',
    'No Director has been found by that name'
  )
  const {
    PaymentApi: {
      fetchInternationalLicensingFilterOptions:
        fetchLicensingFilterOptionsLoadingId,
    },
  } = useLoadingIds()

  const { handleError } = useAppSnackbar()

  const initialValues = useRef({
    actor: { value: '', id: '' },
    programType: { value: '', id: '' },
    community: { value: '', id: '' },
  })

  const [filters, setFilters] = useState<BillingFilters>(initialValues.current)
  const [draftFilters, setDraftFilters] = useState<BillingFilters>(
    initialValues.current
  )
  const [actorOptions, setActorOptions] = useState<Array<ActorFilterOption>>([])
  const [communityOptions, setCommunityOptions] = useState<
    Array<CommunityFilterOption>
  >([])

  const getActorAndCommunityOptions = async () => {
    try {
      const { communities, actors } =
        await paymentsApi.fetchInternationalLicensingFilterOptions({
          actorName: draftFilters.actor.value,
          communityName: draftFilters.community.value,
          actorKey,
        })
      setCommunityOptions(communities)
      setActorOptions(actors ?? [])
    } catch (error) {
      await handleError(
        error,
        t(
          'LicensingFiltersModal.LicensingFilterOptions.ValidationMessage.Error',
          'Something went wrong while fetching the filter options'
        )
      )
    }
  }
  const { triggerFetch: fetchAutocompleteOptions } = useLoadingContext({
    asyncFunction: getActorAndCommunityOptions,
    loadingId: fetchLicensingFilterOptionsLoadingId,
  })

  useEffect(() => {
    if (isOpen) {
      setDraftFilters(filters)
    }
  }, [isOpen, filters])

  const handleFilterChange: OnFilterChange = (name, { value, id }) => {
    setDraftFilters((prevFilters) => ({
      ...prevFilters,
      [name]: {
        value: 'All Program Types'.includes(value) ? '' : value,
        id: 'All Program Types'.includes(value) ? '' : id,
      },
    }))
  }

  useImperativeHandle(ref, () => ({
    clearFilter(filterName: keyof BillingFilters) {
      const newValue = {
        ...filters,
        [filterName]: '',
      }
      setFilters(newValue)
      onFilter(newValue)
    },
  }))

  const handleFilterApply = () => {
    setFilters(draftFilters)
    onFilter(draftFilters)
    setActorOptions([])
    setCommunityOptions([])
    onClose()
  }

  const handleClose = () => {
    setActorOptions([])
    setCommunityOptions([])
    onClose()
  }

  const isClearAllFilterButtonVisible = Object.values(filters).some(
    ({ value }) => value !== ''
  )

  const DialogContent = () => {
    return (
      <Box>
        {showActorFilter && (
          <Box pb={3.5} key="actorField" width="100%">
            <DebouncedAutocomplete
              data-testid={'available-actors'}
              id={'available-actors'}
              options={actorOptions}
              handleChange={(selection: ActorFilterOption | null) => {
                handleFilterChange('actor', {
                  value: formatActorOption(selection),
                  id: `${selection?.actorKey ?? 0}`,
                })
              }}
              noOptionsTextValue={defaultNoActorFoundMessage}
              initialValue={
                !!filters.actor.value
                  ? ({
                      name: filters.actor.value,
                      actorKey: +filters.actor.id,
                    } as ActorFilterOption)
                  : undefined
              }
              optionsLabel={(option: ActorFilterOption) => option.name}
              renderOptions={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: ActorFilterOption
              ) => (
                <Box component="li" {...props} key={option.actorKey}>
                  {formatActorOption(option)}
                </Box>
              )}
              updateSearchResults={(search) => {
                handleFilterChange('actor', {
                  value: search,
                  id: search,
                })
                fetchAutocompleteOptions()
              }}
              textFieldLabelValue={defaultSearchActorLabel}
            />
          </Box>
        )}
        <Box pb={3} key="programField" width="100%">
          <TextField
            name="programType"
            label={t(
              'LicensingFiltersModal.Label.ProgramType',
              'All Program Types'
            )}
            value={draftFilters.programType.value}
            onChange={(e) =>
              handleFilterChange('programType', {
                value: e.target.value,
                id: e.target.value,
              })
            }
            variant="filled"
            fullWidth
            select
          >
            {[
              t(
                'LicensingFiltersModal.Label.AllProgramTypes',
                'All Program Types'
              ),
              ...Object.values(ProgramType),
            ].map((programType) => (
              <MenuItem key={`programType-${programType}`} value={programType}>
                {programType}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box pb={3} key="communityField" width="100%">
          <DebouncedAutocomplete
            id={'available-communities'}
            options={communityOptions}
            handleChange={(selection: CommunityFilterOption | null) => {
              handleFilterChange('community', {
                value: selection?.name ?? '',
                id: `${selection?.communityKey ?? 0}`,
              })
            }}
            initialValue={
              !!filters.community.value
                ? ({
                    name: filters.community.value,
                    communityKey: +filters.community.id,
                  } as CommunityFilterOption)
                : undefined
            }
            noOptionsTextValue={t(
              'LicensingFiltersModal.CommunitySearch.NoResults',
              'No Community has been found by that name'
            )}
            optionsLabel={(option: CommunityFilterOption) => option.name}
            renderOptions={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: CommunityFilterOption
            ) => (
              <Box component="li" {...props} key={option.communityKey}>
                {option.name}
              </Box>
            )}
            updateSearchResults={(search) => {
              handleFilterChange('community', {
                value: search,
                id: search,
              })
              fetchAutocompleteOptions()
            }}
            textFieldLabelValue={t(
              'LicensingFiltersModal.CommunitySearch.Label',
              'Search a Community'
            )}
          />
        </Box>
      </Box>
    )
  }

  const DialogTitle = () => {
    return t('LicensingFiltersModal.Title.FilterLicense', 'Filter License')
  }

  const DialogActions = () => {
    return (
      <ActionButtons
        primaryButtonLabel={ContainedButtonVariant.Filter}
        secondaryClick={() => {
          /**
           * update the internal reference and propagate
           * the update to the parent component
           */
          setFilters(initialValues.current)
          onFilter(initialValues.current)
        }}
        secondaryButtonLabel={
          isClearAllFilterButtonVisible
            ? TextButtonVariant.ClearAllFilters
            : undefined
        }
        tertiaryButtonLabel={TextButtonVariant.Cancel}
        tertiaryClick={handleClose}
        alwaysStack
        useBaseButton
      />
    )
  }

  return (
    <BasicModal
      isOpen={isOpen}
      handleFormSubmit={handleFilterApply}
      maxWidth="xs"
      dialogContent={DialogContent()}
      dialogActions={DialogActions()}
      ariaLabel={DialogTitle()}
      dialogTitle={DialogTitle()}
      labelledBy="LicensingFiltersModal-filters"
    />
  )
})

export default LicensingFiltersModal
